import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { CLAUSE_STATUS, fmtDate } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader, ToneChip } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";

const CATEGORIES = ["responsabilidad", "seguro", "confidencialidad", "proteccion_datos", "propiedad_intelectual", "pagos", "terminacion", "fuerza_mayor", "garantias", "compliance", "anticorrupcion", "jurisdiccion", "subcontratacion", "cesion", "comunicaciones", "auditoria"];

export default function Clausulas() {
  const { map } = useUsers();
  const qc = useQueryClient();
  const [cat, setCat] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "responsabilidad", text: "" });
  const [expanded, setExpanded] = useState(null);

  const { data: items, isLoading, refetch } = useQuery({
    queryKey: ["clauses", cat],
    queryFn: async () => (await api.get("/legal/clauses", { params: { category: cat || undefined } })).data,
  });

  const setStatus = async (id, status) => {
    try {
      await api.post(`/legal/clauses/${id}/status`, { status });
      toast.success("Estado actualizado");
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  const create = async () => {
    try {
      await api.post("/legal/clauses", form);
      toast.success("Cláusula creada en Borrador");
      setNewOpen(false);
      setForm({ name: "", category: "responsabilidad", text: "" });
      refetch();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="clausulas-page">
      <PageHeader
        title="Biblioteca de cláusulas"
        subtitle="Cláusulas corporativas aprobadas. Las cláusulas retiradas no pueden usarse como redacción vigente."
        actions={<Button onClick={() => setNewOpen(true)} data-testid="new-clause-button"><Plus className="mr-1 h-4 w-4" /> Nueva cláusula</Button>}
      />
      <div className="mb-4 flex flex-wrap gap-1.5">
        <button onClick={() => setCat("")} className={`ts-chip ${!cat ? "ts-chip-info" : "ts-chip-muted"}`} data-testid="clause-cat-all">Todas</button>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCat(c)} className={`ts-chip ${cat === c ? "ts-chip-info" : "ts-chip-muted"}`} data-testid={`clause-cat-${c}`}>{c.replace(/_/g, " ")}</button>
        ))}
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-slate-500">Cargando biblioteca…</p>
      ) : !items?.length ? (
        <EmptyState title="Sin cláusulas" description="No hay cláusulas en esta categoría." testid="clauses-empty" />
      ) : (
        <div className="space-y-2">
          {items.map((cl) => (
            <div key={cl.id} className="ts-card p-4" data-testid={`clause-${cl.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setExpanded(expanded === cl.id ? null : cl.id)} className="min-w-0 flex-1 truncate text-left text-sm font-medium text-slate-900 hover:text-blue-700" data-testid={`clause-expand-${cl.id}`}>
                  {cl.name}
                </button>
                <Chip map={CLAUSE_STATUS} value={cl.status} testid={`clause-status-${cl.id}`} />
                <ToneChip label={cl.category.replace(/_/g, " ")} tone="info" />
                <ToneChip label={`v${cl.version} · ${cl.language.toUpperCase()}`} tone="muted" />
                <ToneChip label={`${cl.usage_count} contratos`} tone="muted" />
              </div>
              {expanded === cl.id && (
                <div className="mt-3 border-t border-slate-100 pt-3" data-testid={`clause-detail-${cl.id}`}>
                  <p className="rounded-sm bg-slate-50 p-3 text-sm leading-relaxed text-slate-700">{cl.text}</p>
                  <div className="mt-2 grid gap-1 text-xs text-slate-500 sm:grid-cols-2">
                    <span>Propietario: <strong>{userName(map, cl.owner_id)}</strong></span>
                    <span>Aprobada: {fmtDate(cl.approved_at)}</span>
                    <span>Ámbito: {cl.scope}</span>
                    <span>Riesgo: {cl.risk_level}</span>
                    <span className={cl.status === "retirada" ? "font-semibold text-red-600" : ""}>Uso permitido: {cl.allowed_use}</span>
                    {cl.successor_id && <span>Sustituida por: {cl.successor_id}</span>}
                  </div>
                  {(cl.comments || []).map((cm, i) => <p key={i} className="mt-1 text-xs text-slate-500">Comentario jurídico: {cm.text}</p>)}
                  <div className="mt-3 flex gap-1.5">
                    {cl.status === "borrador" && <Button size="sm" variant="outline" onClick={() => setStatus(cl.id, "en_revision")} data-testid={`clause-review-${cl.id}`}>Enviar a revisión</Button>}
                    {cl.status === "en_revision" && <Button size="sm" onClick={() => setStatus(cl.id, "aprobada")} data-testid={`clause-approve-${cl.id}`}>Aprobar</Button>}
                    {cl.status === "aprobada" && <Button size="sm" variant="destructive" onClick={() => setStatus(cl.id, "retirada")} data-testid={`clause-retire-${cl.id}`}>Retirar</Button>}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-clause-dialog">
          <DialogHeader><DialogTitle>Nueva cláusula</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="clause-name-input" /></div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="clause-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Texto aprobado *</Label><Textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={5} data-testid="clause-text-input" /></div>
            <Button className="w-full" disabled={!form.name.trim() || !form.text.trim()} onClick={create} data-testid="clause-create-submit">Crear en Borrador</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
